import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  IconButton,
  Chip,
  Snackbar,
} from '@mui/material';
import {
  AutoAwesome,
  Refresh,
  FavoriteBorder,
  Favorite,
  FormatQuote,
  CalendarToday,
  MenuBook,
} from '@mui/icons-material';
import { getTodayDevotional, getRandomDevotional } from '../services/api';
import { useNavigate } from 'react-router-dom';

// ── Helpers ──────────────────────────────────────────────────────────────────

function formatDate(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  return d.toLocaleDateString('en-KE', {
    weekday: 'long', year: 'numeric',
    month: 'long',   day: 'numeric',
  });
}

// Soft gradient backgrounds that rotate on random
const CARD_GRADIENTS = [
  'linear-gradient(135deg, rgba(74,222,128,0.12), rgba(34,211,238,0.06))',
  'linear-gradient(135deg, rgba(167,139,250,0.12), rgba(74,222,128,0.06))',
  'linear-gradient(135deg, rgba(251,191,36,0.10), rgba(74,222,128,0.06))',
  'linear-gradient(135deg, rgba(244,114,182,0.10), rgba(167,139,250,0.06))',
  'linear-gradient(135deg, rgba(34,211,238,0.12), rgba(167,139,250,0.06))',
];

let gradientIdx = 0;

// ── Main Component ────────────────────────────────────────────────────────────

export default function Devotional() {
  const navigate = useNavigate();

  const [devotional, setDevotional]   = useState(null);
  const [loading,    setLoading]      = useState(true);
  const [refreshing, setRefreshing]   = useState(false);
  const [error,      setError]        = useState('');
  const [liked,      setLiked]        = useState(false);
  const [gradient,   setGradient]     = useState(CARD_GRADIENTS[0]);
  const [toast,      setToast]        = useState('');

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) { navigate('/login'); return; }
    loadToday();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  async function loadToday() {
    try {
      setLoading(true); setError('');
      const res = await getTodayDevotional();
      setDevotional(res.data);
      setLiked(res.data?.liked ?? false);
    } catch {
      setError('Could not load today\'s devotional. Try again.');
    } finally {
      setLoading(false);
    }
  }

  async function loadRandom() {
    try {
      setRefreshing(true); setError('');
      gradientIdx = (gradientIdx + 1) % CARD_GRADIENTS.length;
      setGradient(CARD_GRADIENTS[gradientIdx]);
      const res = await getRandomDevotional();
      setDevotional(res.data);
      setLiked(res.data?.liked ?? false);
      setToast('New reflection loaded ✨');
    } catch {
      setError('Could not load a new devotional. Try again.');
    } finally {
      setRefreshing(false);
    }
  }

  // Parse devotional fields flexibly
  const title      = devotional?.title      ?? devotional?.topic      ?? 'Daily Reflection';
  const scripture  = devotional?.scripture  ?? devotional?.verse      ?? devotional?.bible_verse ?? '';
  const reference  = devotional?.reference  ?? devotional?.verse_ref  ?? devotional?.book ?? '';
  const reflection = devotional?.reflection ?? devotional?.content    ?? devotional?.body ?? '';
  const prayer     = devotional?.prayer     ?? devotional?.closing_prayer ?? '';
  const date       = devotional?.date       ?? devotional?.created_at ?? '';
  const category   = devotional?.category   ?? devotional?.theme      ?? '';

  return (
    <Box sx={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #0a1628 0%, #0d1f3c 50%, #0a2040 100%)',
      px: { xs: 2, sm: 4 },
      py: 4,
      color: '#e8f4f8',
    }}>

      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 4 }}>
        <Box>
          <Typography sx={{
            fontSize: { xs: '1.8rem', sm: '2.2rem' },
            fontWeight: 800,
            background: 'linear-gradient(90deg, #4ade80, #22d3ee)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            letterSpacing: '-0.5px',
          }}>
            Devotional
          </Typography>
          <Typography sx={{ color: '#7fa8c8', fontSize: '0.9rem', mt: 0.5 }}>
            Nourish your spirit daily 🙏
          </Typography>
        </Box>

        {/* Random button */}
        <IconButton
          onClick={loadRandom}
          disabled={refreshing || loading}
          title="Load a random devotional"
          sx={{
            background: 'rgba(74,222,128,0.1)',
            border: '1px solid rgba(74,222,128,0.25)',
            color: '#4ade80',
            '&:hover': { background: 'rgba(74,222,128,0.2)' },
            '&.Mui-disabled': { opacity: 0.4 },
          }}
        >
          {refreshing
            ? <CircularProgress size={20} sx={{ color: '#4ade80' }} />
            : <Refresh />}
        </IconButton>
      </Box>

      {/* Loading */}
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
          <CircularProgress sx={{ color: '#4ade80' }} />
        </Box>
      )}

      {/* Error */}
      {error && !loading && (
        <Alert severity="error" sx={{
          mb: 3,
          background: 'rgba(211,47,47,0.12)',
          color: '#ef9a9a',
          border: '1px solid rgba(211,47,47,0.3)',
        }}>
          {error}
        </Alert>
      )}

      {/* No content */}
      {!loading && !error && !devotional && (
        <Box sx={{ textAlign: 'center', py: 10 }}>
          <MenuBook sx={{ fontSize: 64, color: '#3a5a7a', mb: 2 }} />
          <Typography sx={{ color: '#7fa8c8', mb: 2 }}>
            No devotional available today.
          </Typography>
          <IconButton onClick={loadRandom} sx={{ color: '#4ade80' }}>
            <Refresh /> &nbsp;Try a random one
          </IconButton>
        </Box>
      )}

      {/* Devotional card */}
      {!loading && devotional && (
        <Box sx={{ maxWidth: 680, mx: 'auto' }}>

          {/* Date + category row */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2.5, flexWrap: 'wrap' }}>
            {date && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <CalendarToday sx={{ fontSize: 14, color: '#7fa8c8' }} />
                <Typography sx={{ fontSize: '0.8rem', color: '#7fa8c8' }}>
                  {formatDate(date)}
                </Typography>
              </Box>
            )}
            {category && (
              <Chip label={category} size="small" sx={{
                background: 'rgba(74,222,128,0.1)',
                color: '#4ade80',
                fontSize: '0.7rem',
                height: 22,
                border: '1px solid rgba(74,222,128,0.25)',
              }} />
            )}
          </Box>

          {/* Main card */}
          <Box sx={{
            background: gradient,
            border: '1px solid rgba(74,222,128,0.2)',
            borderRadius: 4,
            p: { xs: 2.5, sm: 4 },
            mb: 2,
            position: 'relative',
          }}>
            {/* Like button */}
            <IconButton
              onClick={() => {
                setLiked(l => !l);
                setToast(liked ? 'Removed from favourites' : 'Added to favourites 💚');
              }}
              sx={{
                position: 'absolute', top: 16, right: 16,
                color: liked ? '#f472b6' : '#3a5a7a',
                '&:hover': { color: '#f472b6' },
              }}
            >
              {liked ? <Favorite /> : <FavoriteBorder />}
            </IconButton>

            {/* Title */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2.5, pr: 4 }}>
              <AutoAwesome sx={{ fontSize: 20, color: '#fbbf24' }} />
              <Typography sx={{
                fontSize: { xs: '1.2rem', sm: '1.4rem' },
                fontWeight: 800,
                color: '#fff',
                lineHeight: 1.3,
              }}>
                {title}
              </Typography>
            </Box>

            {/* Scripture block */}
            {scripture && (
              <Box sx={{
                background: 'rgba(255,255,255,0.05)',
                border: '1px solid rgba(74,222,128,0.15)',
                borderLeft: '3px solid #4ade80',
                borderRadius: '0 12px 12px 0',
                p: 2.5,
                mb: 3,
                position: 'relative',
              }}>
                <FormatQuote sx={{
                  position: 'absolute', top: 8, right: 12,
                  fontSize: 32, color: 'rgba(74,222,128,0.2)',
                  transform: 'scaleX(-1)',
                }} />
                <Typography sx={{
                  fontSize: { xs: '1rem', sm: '1.1rem' },
                  color: '#d4e8f0',
                  fontStyle: 'italic',
                  lineHeight: 1.8,
                  mb: reference ? 1 : 0,
                }}>
                  "{scripture}"
                </Typography>
                {reference && (
                  <Typography sx={{
                    fontSize: '0.8rem',
                    fontWeight: 700,
                    color: '#4ade80',
                  }}>
                    — {reference}
                  </Typography>
                )}
              </Box>
            )}

            {/* Reflection */}
            {reflection && (
              <Box sx={{ mb: 3 }}>
                <Typography sx={{
                  fontSize: '0.7rem',
                  fontWeight: 700,
                  color: '#22d3ee',
                  textTransform: 'uppercase',
                  letterSpacing: 2,
                  mb: 1,
                }}>
                  Reflection
                </Typography>
                <Typography sx={{
                  fontSize: '0.95rem',
                  color: '#d4e8f0',
                  lineHeight: 1.85,
                  whiteSpace: 'pre-wrap',
                }}>
                  {reflection}
                </Typography>
              </Box>
            )}

            {/* Prayer */}
            {prayer && (
              <Box sx={{
                background: 'rgba(167,139,250,0.08)',
                border: '1px solid rgba(167,139,250,0.2)',
                borderRadius: 3,
                p: 2,
              }}>
                <Typography sx={{
                  fontSize: '0.7rem',
                  fontWeight: 700,
                  color: '#a78bfa',
                  textTransform: 'uppercase',
                  letterSpacing: 2,
                  mb: 1,
                }}>
                  🙏 Prayer
                </Typography>
                <Typography sx={{
                  fontSize: '0.9rem',
                  color: '#d4e8f0',
                  lineHeight: 1.75,
                  fontStyle: 'italic',
                }}>
                  {prayer}
                </Typography>
              </Box>
            )}
          </Box>

          {/* Bottom hint */}
          <Box sx={{
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            gap: 1, mt: 3,
          }}>
            <Refresh sx={{ fontSize: 14, color: '#3a5a7a' }} />
            <Typography sx={{ fontSize: '0.75rem', color: '#3a5a7a' }}>
              Tap the refresh icon for a new reflection
            </Typography>
          </Box>
        </Box>
      )}

      {/* Toast */}
      <Snackbar
        open={!!toast}
        autoHideDuration={2500}
        onClose={() => setToast('')}
        message={toast}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        sx={{
          '& .MuiSnackbarContent-root': {
            background: 'rgba(74,222,128,0.15)',
            color: '#4ade80',
            border: '1px solid rgba(74,222,128,0.3)',
            borderRadius: 2,
          },
        }}
      />
    </Box>
  );
}